<template>
  <div class="landing-wrapper">
    <v-container class="py-8 py-md-12" fluid>
      <v-row justify="center">
        <v-col cols="12" lg="10">
          <v-alert v-if="error" type="error" class="mb-4" closable @click:close="error = ''">
            {{ error }}
          </v-alert>

          <div v-if="loading" class="d-flex justify-center py-8">
            <v-progress-circular indeterminate color="primary" size="48" />
          </div>

          <template v-else>
            <div class="sp-toolbar">
              <h2 class="sp-heading">SOPORTES DE OBRA</h2>
              <!-- Sólo la administración mantiene el catálogo. -->
              <button v-if="isAdmin" type="button" class="sp-new-btn" @click="goToNew">
                CREAR NUEVO SOPORTE
              </button>
            </div>

            <div class="sp-panel">
              <div class="sp-panel-header">
                <v-icon size="22" color="#ffffff">mdi-lifebuoy</v-icon>
                <span>SOPORTES</span>
              </div>

              <div class="sp-panel-body">
                <div v-if="supports.length === 0" class="sp-empty">
                  Todavía no hay soportes registrados.
                </div>

                <div
                  v-for="s in supports"
                  :key="s.id"
                  class="sp-block"
                  :class="{ 'sp-block-off': !s.active }"
                >
                  <div class="sp-block-head">
                    <div>
                      <h3 class="sp-block-title">
                        {{ s.name }}
                        <span v-if="!s.active" class="sp-tag">INACTIVO</span>
                      </h3>
                      <p v-if="s.description" class="sp-block-desc">{{ s.description }}</p>
                    </div>
                    <button
                      v-if="isAdmin"
                      type="button"
                      class="sp-row-btn"
                      @click="goToEdit(s)"
                    >
                      VER / EDITAR
                    </button>
                  </div>

                  <div class="sp-table-scroll">
                    <table class="sp-table">
                      <thead>
                        <tr>
                          <th>NOMBRE</th>
                          <th>DESCRIPCIÓN</th>
                          <th>TELÉFONO</th>
                          <th>CORREO</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="c in activeContacts(s)" :key="c.id">
                          <td class="sp-cell-name">{{ c.name }}</td>
                          <td>{{ c.description || '—' }}</td>
                          <td>
                            <a v-if="c.phone" :href="`tel:${c.phone}`" class="sp-link">{{ c.phone }}</a>
                            <span v-else>—</span>
                          </td>
                          <td>
                            <a v-if="c.email" :href="`mailto:${c.email}`" class="sp-link">{{ c.email }}</a>
                            <span v-else>—</span>
                          </td>
                        </tr>
                        <tr v-if="activeContacts(s).length === 0">
                          <td colspan="4" class="sp-empty-row">
                            Este soporte todavía no tiene contactos.
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </template>
        </v-col>
      </v-row>
    </v-container>

    <teatro-footer />
  </div>
</template>

<script>
import { fetchSupports } from "@/services/teatroApi";

export default {
  name: "SupportList",
  data() {
    return {
      supports: [],
      loading: true,
      error: "",
    };
  },
  computed: {
    isAdmin() {
      return this.$store?.state?.is_admin === true;
    },
  },
  async created() {
    await this.loadAuth();
    await this.load();
  },
  methods: {
    async loadAuth() {
      try {
        await this.$store.dispatch("fetchIsAuthenticated");
      } catch (e) {
        console.error(e);
      }
    },
    async load() {
      this.error = "";
      try {
        this.supports = (await fetchSupports()) || [];
      } catch (e) {
        this.error = "No se pudieron cargar los soportes.";
        console.error(e);
      } finally {
        this.loading = false;
      }
    },
    // Las compañías sólo ven contactos vigentes; el admin los ve todos para
    // poder reactivarlos.
    activeContacts(support) {
      const contacts = support.contacts || [];
      return this.isAdmin ? contacts : contacts.filter((c) => c.active !== false);
    },
    goToNew() {
      window.location.href = "/soportes/nuevo/";
    },
    goToEdit(s) {
      window.location.href = `/soportes/${s.id}/editar/`;
    },
  },
};
</script>

<style scoped>
.sp-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 18px;
  max-width: 1080px;
  margin: 0 auto 22px;
  flex-wrap: wrap;
}

.sp-heading {
  color: var(--color-theater-text);
  font-family: var(--font-family-heading);
  font-size: 22px;
  font-weight: 800;
  letter-spacing: 1.2px;
  margin: 0;
}

.sp-new-btn {
  background: linear-gradient(90deg, #c8102e 0%, #a50d25 100%);
  color: #fff;
  border: none;
  border-radius: 8px;
  padding: 12px 24px;
  font-family: var(--font-family-heading);
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 1px;
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.sp-new-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 8px 18px rgba(200, 16, 46, 0.4);
}

.sp-panel {
  max-width: 1080px;
  margin: 0 auto;
  background: rgba(8, 5, 5, 0.66);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 26px;
  overflow: hidden;
  box-shadow: 0 24px 60px rgba(0, 0, 0, 0.55);
}

.sp-panel-header {
  display: flex;
  align-items: center;
  gap: 12px;
  height: 66px;
  padding: 0 32px;
  background: linear-gradient(90deg, #c8102e 0%, #a50d25 100%);
  color: #fff;
  font-family: var(--font-family-heading);
  font-size: 15px;
  font-weight: 800;
  letter-spacing: 1.4px;
}

.sp-panel-body {
  padding: 26px 34px 30px;
}

.sp-block + .sp-block {
  margin-top: 30px;
  padding-top: 26px;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
}

.sp-block-off {
  opacity: 0.55;
}

.sp-block-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 18px;
  margin-bottom: 14px;
}

.sp-block-title {
  color: var(--color-theater-text);
  font-family: var(--font-family-heading);
  font-size: 16px;
  font-weight: 800;
  letter-spacing: 1px;
  text-transform: uppercase;
  margin: 0;
}

.sp-block-desc {
  color: var(--color-theater-text-muted);
  font-family: var(--font-family-heading);
  font-size: 12px;
  font-weight: 600;
  margin: 4px 0 0;
}

.sp-tag {
  display: inline-block;
  margin-left: 8px;
  padding: 2px 8px;
  border-radius: 999px;
  background: rgba(200, 16, 46, 0.25);
  color: #ff8ea0;
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 0.6px;
}

.sp-row-btn {
  flex: 0 0 auto;
  background: linear-gradient(90deg, #c8102e 0%, #a50d25 100%);
  color: #fff;
  border: none;
  border-radius: 6px;
  padding: 9px 18px;
  font-family: var(--font-family-heading);
  font-size: 10.5px;
  font-weight: 800;
  letter-spacing: 0.8px;
  white-space: nowrap;
  cursor: pointer;
  transition: transform 0.2s ease;
}

.sp-row-btn:hover {
  transform: translateY(-1px);
}

.sp-table-scroll {
  overflow-x: auto;
}

.sp-table {
  width: 100%;
  border-collapse: collapse;
  color: var(--color-theater-text);
  font-family: var(--font-family-heading);
}

.sp-table th {
  text-align: left;
  padding: 8px 16px 14px;
  font-size: 10.5px;
  font-weight: 700;
  letter-spacing: 1px;
  color: rgba(255, 255, 255, 0.55);
  white-space: nowrap;
}

.sp-table td {
  padding: 13px 16px;
  font-size: 12.5px;
  font-weight: 600;
  letter-spacing: 0.4px;
  vertical-align: middle;
  border-top: 1px solid rgba(255, 255, 255, 0.06);
}

.sp-cell-name {
  text-transform: uppercase;
}

.sp-link {
  color: #6fb7ea;
  text-decoration: none;
}

.sp-link:hover {
  text-decoration: underline;
}

.sp-empty,
.sp-empty-row {
  text-align: center;
  padding: 26px 16px;
  color: rgba(255, 255, 255, 0.6);
  font-family: var(--font-family-heading);
  font-size: 13px;
  font-weight: 500;
}

@media (max-width: 700px) {
  .sp-toolbar {
    justify-content: center;
    text-align: center;
  }
}
</style>
